import type { MemSearchProductThemeName, MemSearchProductThemeType } from '../api/memsearch/typesMemsearch.ts';

export enum VOUCHER_EXTRA_TYPE {
  NONE = 0,
  THIRTY = 1,
  HUNDRED = 2,
}

export enum VOUCHER_TYPE {
  PERCENTAGE = 'percentage',
  FIXED = 'fixed',
}

export type ProductAccommodationFacilities = {
  adultsOnly: boolean;
  children: boolean;
  disableGuest: boolean;
  entertainment: boolean;
  freeWifi: boolean;
  healthSafety: boolean;
  kitchen: boolean;
  parking: boolean;
  petsAllowed: boolean;
  pool: boolean;
  seafront: boolean;
  spa: boolean;
  generalEvCharge?: boolean;
  generalFitness?: boolean;
  generalRooftopTerrace?: boolean;
  familyChildrenPlayground?: boolean;
  familyBabySitting?: boolean;
  familyChildrenClubs?: boolean;
  familyChildrenMeal?: boolean;
  familyChildrenPool?: boolean;
  activitiesHiking?: boolean;
  activitiesFishing?: boolean;
  activitiesCycling?: boolean;
  activitiesBoardGames?: boolean;
  activitiesHorseRiding?: boolean;
  activitiesGolf?: boolean;
  activitiesBicycleRental?: boolean;
  activitiesCanoeing?: boolean;
  activitiesDiving?: boolean;
  activitiesWindsurfing?: boolean;
  activitiesWaterPark?: boolean;
  activitiesSnorkelling?: boolean;
  activitiesBowling?: boolean;
  activitiesBilliards?: boolean;
  activitiesScuba?: boolean;
  activitiesMassage?: boolean;
  activitiesWaterSport?: boolean;
  activitiesEveningEntertainment?: boolean;
  activitiesTennisCourt?: boolean;
  activitiesArcadeRoom?: boolean;
  activitiesKayaking?: boolean;
  activitiesEcotours?: boolean;
  activitiesYoga?: boolean;
  activitiesNightclub?: boolean;
  activitiesDarts?: boolean;
  activitiesPlayArea?: boolean;
  spaIndoorPool?: boolean;
  spaRooftopPool?: boolean;
  spaHammam?: boolean;
  spaSauna?: boolean;
  spaSteamRoom?: boolean;
  spaSaltPool?: boolean;
  spaInfinityPool?: boolean;
  spaWaterSlide?: boolean;
  foodSunTerrace?: boolean;
  foodPicnic?: boolean;
  foodBar?: boolean;
  foodPoolBar?: boolean;
  foodRestaurant?: boolean;
  foodBbq?: boolean;
  foodMinimarket?: boolean;
};

export type ProductThemeType = MemSearchProductThemeType;
export type ProductThemeName = MemSearchProductThemeName;

export type ProductTheme = {
  name: ProductThemeName;
  score: number;
  type: ProductThemeType;
};

export type ProductAccommodation = {
  address: string;
  chainName: string;
  comvelId: number;
  orchestraId: number;
  facilities: ProductAccommodationFacilities;
  gallery: string[];
  image: string;
  hotelId: number;
  isAlternative: boolean;
  latitude: number;
  longitude: number;
  name: string;
  providerId: number;
  rating: number;
  reviewsNum: number;
  ratingSource?: string;
  stars: number;
  type: string;
  typeId: number;
  toCode?: string;
  toName?: string;
  themes: ProductTheme[];
};

export type ProductDatesAlternative = {
  dateFrom: string;
  dateTo: string;
  price: number;
  strikethrough: number;
};

export type ProductDates = {
  accNights?: number;
  alternative: boolean | ProductDatesAlternative[];
  checkin?: string;
  checkout?: string;
  dateFrom?: string;
  dateTo?: string;
  nights?: number;
  soldOut: boolean;
  alternativeDeals?: boolean;
};

export type DistanceUspDetail = {
  distanceM: number;
  name: string;
};

export type DistanceUsp = {
  nearestBeach?: DistanceUspDetail;
  nearestSkiLift?: DistanceUspDetail;
};

export type ProductGeo = {
  cityDisplayName: string;
  cityName: string;
  countryCode: string;
  countryName: string;
  destination?: string;
  destinationAp?: string;
  destinationApName?: string;
  destinationName?: string;
  distanceKm: number;
  geoDBId?: string;
  internalIdCity: number;
  leisure: number;
  origin?: string;
  originAp?: string;
  originApName?: string;
  originName?: string;
  tagId: string | number;
  tagIds: string;
  distanceUsp?: DistanceUsp;
};

export type ProductVoucher = {
  bookUntil: string;
  code: string;
  description: string;
  discountCurrency: string;
  discountType: VOUCHER_TYPE;
  discountValue: number;
  tcUrl: string;
  uspTitle?: string;
  title: string;
};

export type ProductPromotions = {
  blackFriday: boolean;
  flashSale: boolean;
  flashSaleExpiration: string;
  londonEye: boolean;
  tourFrance: boolean;
  voucher?: ProductVoucher;
};

export type ProductRateFeatures = {
  cancellation: boolean;
  bookNowPayLater: boolean;
  mealPlan: number;
  rooms: number;
};

export type ProductRateFlags = {
  apex: boolean;
  fallback: boolean;
  livectx: boolean;
  loyalty: boolean;
  mobile: boolean;
  myPrice: boolean;
  opx: boolean;
  pinkPeople: boolean;
  plus: boolean;
  tmpStatefulSendSearchId: boolean;
  tsh: boolean;
  voucherExtraType: VOUCHER_EXTRA_TYPE;
  // voucher: boolean;
};

export type ProductRateOffers = {
  januarySale: boolean;
  skiPass: boolean;
  spa: boolean;
  tickets: boolean;
  transfer: boolean;
  resortCredit?: boolean;
  childrenFree?: boolean;
  welcomeGift?: boolean;
};

export type ProductRatePackageDiscount = {
  enabled: boolean;
  totalPriceFo: number;
  totalPriceHo: number;
};

export type ProductRatePriceTotal = {
  depositAmount: number;
  price: number;
  priceEur: number;
  strikethrough: number;
  strikethroughEur: number;
};

export type ProductRatePrice = {
  age: number;
  currency: string;
  isPerPerson: boolean;
  depositAmount: number;
  price: number;
  showDiscount: boolean;
  strikethrough: number;
  taxes: number;
  taxesTotal: number;
  total: ProductRatePriceTotal;
};

export type ProductRatePricePlusTotal = {
  price: number;
  strikethrough: number;
};

export type ProductRatePricePlus = {
  age: number;
  currency: string;
  isPerPerson: boolean;
  price: number;
  showDiscount: boolean;
  strikethrough: number;
  taxes: number;
  taxesTotal: number;
  total: ProductRatePricePlusTotal;
};

export type ProductRateTep = {
  cost: number;
  standardPrice: number;
  discount: number;
  enabled: boolean;
  voucher: number;
};

export type PricingRules = {
  hotelListingPriceId: number;
  hotelListingPriceRuleId: number;
  hotelListingRateType: string;
  markupPriceRruleId: number;
  markupRuleId: number;
};

export type PricingDebug = {
  opaqueRate: boolean;
  hotelCost: number;
  expectedRevenues: number;
  markup: number;
  despAmount: number;
  lunikRevenue: number;
};

export type ProductDebug = {
  businessProfile?: string;
  initiator?: string;
  live_search_hash?: number;
  opxSortPriority?: number;
  priceSource?: string;
  pricingRules?: PricingRules;
  rankingDestination?: number;
  rankingDebugMan?: number;
  rankingMan?: number;
  rankingPosAbsolute?: number;
  rankingPosAdjusted?: number;
  rankingPosOriginal?: number;
  rankingPsq?: number;
  rankingSa?: number;
  rankingSaType?: string;
  rankingType?: string;
  rtcoEnabled?: boolean;
  rtcoScore?: number;
  rtcoVariation?: number;
  traceId?: string;
};

export type ProductDebugServerOnly = {
  pricingDebug?: PricingDebug;
};

export type ProductRateContext = {
  priceContext?: string;
  signature?: string;
};

export type ProductRate = {
  features: ProductRateFeatures;
  flags: ProductRateFlags;
  offers: ProductRateOffers;
  packageDiscount: ProductRatePackageDiscount;
  price: ProductRatePrice;
  pricePlus: ProductRatePricePlus;
  tep: ProductRateTep;
  context?: ProductRateContext;
};

export type ProductTransport = {
  carrier: string;
  carrierName?: string;
  direct: boolean;
  nextDay: boolean;
  // reducedCheckout: boolean;
  transportId: string;
  type: string;
};

export type Product = {
  accommodation: ProductAccommodation;
  dates: ProductDates;
  geo: ProductGeo;
  promotions: ProductPromotions;
  rate: ProductRate;
  transport?: ProductTransport;
  debug?: ProductDebug;
};

export type ProductServerOnly = {
  debug?: ProductDebugServerOnly;
};
